import { getServiceTechManifest, stampServiceTechCheckIn } from "@/lib/service/client";
import type { ServiceCheckInMilestone, ServiceTechManifest } from "@/lib/service/types";
import {
  applyCheckInToManifest,
  enqueuePendingServiceTechCheckIn,
  listPendingServiceTechCheckIns,
  milestoneRank,
  readCachedServiceTechManifest,
  removePendingServiceTechCheckIn,
  writeCachedServiceTechManifest,
  type PendingServiceTechCheckIn,
} from "@/lib/service/serviceTechCache";

export type ServiceTechSyncResult = {
  manifest: ServiceTechManifest | null;
  conflicts: string[];
  error: string | null;
  fromCache: boolean;
};

export async function loadServiceTechManifestWithCache(opts?: {
  horizon_days?: number;
}): Promise<ServiceTechSyncResult> {
  const cached = await readCachedServiceTechManifest();
  if (!navigator.onLine) {
    return { manifest: cached, conflicts: [], error: null, fromCache: true };
  }

  const remote = await getServiceTechManifest(opts);
  if (!remote.ok) {
    return {
      manifest: cached,
      conflicts: [],
      error: remote.error,
      fromCache: !!cached,
    };
  }

  const manifest = remote.data.manifest;
  await writeCachedServiceTechManifest(manifest);
  const conflicts = await flushPendingServiceTechCheckIns(manifest);
  const refreshed = conflicts.length ? await getServiceTechManifest(opts) : null;
  if (refreshed?.ok) {
    await writeCachedServiceTechManifest(refreshed.data.manifest);
    return {
      manifest: refreshed.data.manifest,
      conflicts,
      error: null,
      fromCache: false,
    };
  }
  return { manifest, conflicts, error: null, fromCache: false };
}

async function flushPendingServiceTechCheckIns(
  serverManifest: ServiceTechManifest
): Promise<string[]> {
  const pending = await listPendingServiceTechCheckIns();
  const conflicts: string[] = [];
  const serverEventById = new Map(serverManifest.events.map((e) => [e.id, e]));

  for (const item of pending) {
    const server = serverEventById.get(item.event_id);
    if (!server) {
      conflicts.push(item.event_id);
      await removePendingServiceTechCheckIn(item.id);
      continue;
    }

    const serverRank =
      server.check_in_status === "none"
        ? 0
        : milestoneRank(server.check_in_status as ServiceCheckInMilestone);
    const queuedRank = milestoneRank(item.milestone);
    if (serverRank > queuedRank) {
      conflicts.push(item.event_id);
      await removePendingServiceTechCheckIn(item.id);
      continue;
    }
    if (serverRank === queuedRank && server.check_in_status === item.milestone) {
      await removePendingServiceTechCheckIn(item.id);
      continue;
    }

    const res = await stampServiceTechCheckInRemote(item);
    if (res.ok) {
      await removePendingServiceTechCheckIn(item.id);
      continue;
    }
    if (res.status === 403 || res.status === 404) {
      conflicts.push(item.event_id);
      await removePendingServiceTechCheckIn(item.id);
    }
  }
  return conflicts;
}

async function stampServiceTechCheckInRemote(item: PendingServiceTechCheckIn) {
  return stampServiceTechCheckIn({
    event_id: item.event_id,
    milestone: item.milestone,
    notes: item.notes || undefined,
    evidence_url: item.evidence_url,
  });
}

export async function stampServiceTechCheckInLocal(opts: {
  manifest: ServiceTechManifest;
  eventId: string;
  milestone: ServiceCheckInMilestone;
  notes?: string;
  evidence_url?: string | null;
}): Promise<{
  manifest: ServiceTechManifest;
  conflicts: string[];
  error: string | null;
}> {
  const optimistic = applyCheckInToManifest(opts.manifest, opts.eventId, opts.milestone);
  await writeCachedServiceTechManifest(optimistic);
  const pending = await enqueuePendingServiceTechCheckIn({
    event_id: opts.eventId,
    milestone: opts.milestone,
    notes: opts.notes ?? "",
    evidence_url: opts.evidence_url ?? null,
  });

  if (!navigator.onLine) {
    return { manifest: optimistic, conflicts: [], error: null };
  }

  const res = await stampServiceTechCheckInRemote(pending);
  if (!res.ok) {
    return { manifest: optimistic, conflicts: [], error: res.error };
  }

  await removePendingServiceTechCheckIn(pending.id);
  const sync = await loadServiceTechManifestWithCache();
  return {
    manifest: sync.manifest ?? optimistic,
    conflicts: sync.conflicts,
    error: sync.error,
  };
}
